import React, { useMemo } from 'react';
import { Col, Row, Typography, List } from 'antd';
import { useQuery } from '@apollo/client';
import { queries } from '../../graphql/graphql';
import {
  DirectoriesValuesByIdsResponse,
  DirectoryResponse,
  DirectoryValue,
} from '../../types/directory';
import { Loader } from '../UI/Loader';

const { Text } = Typography;

interface AvailableAreaProps {
  available: {
    cities: string[];
    states: string[];
    counties: string[];
  };
}

export const AvailableArea = ({ available }: AvailableAreaProps) => {
  const { loading: statesDirectoryLoading, data: statesDirectoryData } =
    useQuery<DirectoryResponse>(queries.directories.DIRECTORY, {
      variables: { slug: 'us-states' },
    });

  const { loading: citiesLoading, data: citiesData } = useQuery<DirectoriesValuesByIdsResponse>(
    queries.directories.DIRECTORIES_VALUES_BY_IDS,
    {
      variables: { ids: available.counties },
      skip: !available.cities || available.cities.length === 0,
    }
  );

  const { loading: countiesLoading, data: countiesData } = useQuery<DirectoriesValuesByIdsResponse>(
    queries.directories.DIRECTORIES_VALUES_BY_IDS,
    {
      variables: { ids: available.states },
      skip: !available.states || available.states.length === 0,
    }
  );

  const selectedStates = useMemo(() => {
    if (!statesDirectoryData?.directory || !available.states?.length) return [];
    const selectedIds = new Set(available.states.map(value => value?.toString()));

    return statesDirectoryData.directory.values.filter(stateValue => {
      const stateId = (stateValue.directory || stateValue.id)?.toString();
      return stateId ? selectedIds.has(stateId) : false;
    });
  }, [statesDirectoryData, available.states]);

  const filterSelected = (
    data: DirectoryValue[] | undefined,
    selected: string[] | undefined
  ): DirectoryValue[] => {
    if (!data || !selected?.length) return [];
    const selectedIds = new Set(selected.map(item => item?.toString()));

    return data.filter(value => {
      const valueId = (value.directory || value.id)?.toString();
      return valueId ? selectedIds.has(valueId) : false;
    });
  };

  const selectedCounties = useMemo(() => {
    return filterSelected(countiesData?.directoriesValuesByIds, available.counties);
  }, [countiesData, available.counties]);

  const selectedCities = useMemo(() => {
    return filterSelected(citiesData?.directoriesValuesByIds, available.cities);
  }, [citiesData, available.cities]);

  if (statesDirectoryLoading || citiesLoading || countiesLoading) return <Loader />;

  return (
    <Row gutter={[16, 16]}>
      {selectedStates.length > 0 ? (
        selectedStates.map(state => {
          const stateId = (state.directory || state.id)?.toString();
          const countiesForState = selectedCounties.filter(
            county => county.parentId?.toString() === stateId
          );

          return (
            <Col span={24} key={stateId}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text strong>{state.name}</Text>
                {countiesForState.length > 0 ? (
                  <List
                    style={{ marginTop: 4 }}
                    dataSource={countiesForState}
                    renderItem={county => <List.Item key={county.id}>{county.name}</List.Item>}
                    size="small"
                  />
                ) : (
                  <Text type="secondary" style={{ marginTop: 4 }}>
                    Available in all counties of this state
                  </Text>
                )}
              </div>
            </Col>
          );
        })
      ) : (
        <Col span={24}>
          <Text>No states selected</Text>
        </Col>
      )}
      {available.cities?.length ? (
        selectedCities.length > 0 ? (
          <Col span={24}>
            <Text strong>Cities:</Text>
            <List
              dataSource={selectedCities}
              renderItem={city => <List.Item key={city.id}>{city.name}</List.Item>}
              size="small"
            />
          </Col>
        ) : (
          <Col span={24}>
            <Text>No cities selected</Text>
          </Col>
        )
      ) : null}
    </Row>
  );
};
